/* eslint-disable no-unused-vars */
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../common/components/LocalizationProvider';
import SelectField from '../../common/components/SelectField';
import SplitButton from '../../common/components/SplitButton';
import { useRestriction } from '../../common/util/permissions';
import { devicesActions, reportsActions } from '../../store';
import useReportStyles from '../common/useReportStyles';
import SearchSelect from './SearchableSelect';

export const updateReportParams = (searchParams, setSearchParams, key, values) => {
  const newParams = new URLSearchParams(searchParams);
  newParams.delete(key);
  newParams.delete('from');
  newParams.delete('to');
  values.forEach((value) => newParams.append(key, value));
  setSearchParams(newParams, { replace: true });
};

// Etiquetas de traducción para cada opción de período disponible.
const PERIOD_LABELS = {
  today: 'reportToday',
  yesterday: 'reportYesterday',
  thisWeek: 'reportThisWeek',
  previousWeek: 'reportPreviousWeek',
  thisMonth: 'reportThisMonth',
  previousMonth: 'reportPreviousMonth',
  custom: 'reportCustom',
  singleDate: 'reportDate',
};

// Conjunto de opciones que se mostraban por defecto antes de este cambio
// (todas menos "singleDate", que antes solo aparecía con customDayOnly).
const DEFAULT_PERIOD_OPTIONS = [
  'today',
  'yesterday',
  'thisWeek',
  'previousWeek',
  'thisMonth',
  'previousMonth',
  'custom',
];

const ReportFilter = ({
  children,
  handleSubmit,
  handleSchedule,
  showOnly,
  ignoreDevice,
  multiDevice,
  includeGroups,
  unified,
  forceDisabled,
  periodOptions = DEFAULT_PERIOD_OPTIONS,
  extra,
}) => {

  const classes = useReportStyles();
  const dispatch = useDispatch();
  const t = useTranslation();

  const readonly = useRestriction('readonly');

  const devices = useSelector((state) => state.devices.items);
  const groups = useSelector((state) => state.groups.items);

  const deviceId = useSelector((state) => state.devices.selectedId);
  const deviceIds = useSelector((state) => state.devices.selectedIds);
  const groupIds = useSelector((state) => state.reports.groupIds);
  const period = useSelector((state) => state.reports.period);
  const from = useSelector((state) => state.reports.from);
  const to = useSelector((state) => state.reports.to);

  const [unify, setUnify] = useState(false);
  const [button, setButton] = useState('json');
  const [description, setDescription] = useState();
  const [calendarId, setCalendarId] = useState();

  const scheduleDisabled = button === 'schedule' && (!description || !calendarId);
  const disabled =
    (!ignoreDevice && !deviceId && !deviceIds.length && !groupIds.length)
    || scheduleDisabled
    || forceDisabled;

  const handleClick = (type) => {
    if (type === 'schedule') {
      handleSchedule(deviceIds, groupIds, {
        description,
        calendarId,
        attributes: {},
      });
      return;
    }

    let selectedFrom;
    let selectedTo;

    switch (period) {
      case 'today':
        selectedFrom = moment().startOf('day');
        selectedTo = moment().endOf('day');
        break;
      case 'yesterday':
        selectedFrom = moment().subtract(1, 'day').startOf('day');
        selectedTo = moment().subtract(1, 'day').endOf('day');
        break;
      case 'thisWeek':
        selectedFrom = moment().startOf('week');
        selectedTo = moment().endOf('week');
        break;
      case 'previousWeek':
        selectedFrom = moment().subtract(1, 'week').startOf('week');
        selectedTo = moment().subtract(1, 'week').endOf('week');
        break;
      case 'thisMonth':
        selectedFrom = moment().startOf('month');
        selectedTo = moment().endOf('month');
        break;
      case 'previousMonth':
        selectedFrom = moment().subtract(1, 'month').startOf('month');
        selectedTo = moment().subtract(1, 'month').endOf('month');
        break;
      case 'singleDate':
        selectedFrom = moment(from).startOf('day');
        selectedTo = moment(from).endOf('day');
        break;
      default:
        selectedFrom = moment(from);
        selectedTo = moment(to);
    }

    handleSubmit({
      deviceId,
      deviceIds,
      groupIds,
      from: selectedFrom.toISOString(),
      to: selectedTo.toISOString(),
      calendarId,
      type,
      unify,
    });
  };

  // Si el período actual en Redux no está entre las opciones permitidas
  // para esta instancia del filtro, se ajusta a la primera disponible.
  useEffect(() => {
    if (periodOptions.length && !periodOptions.includes(period)) {
      dispatch(reportsActions.updatePeriod(periodOptions[0]));
    }
  }, []);

  return (
    <div className={classes.filter}>
      {!ignoreDevice && (
        <div className={classes.filterItem}>
          <FormControl fullWidth>
            <SearchSelect
              data={Object.values(devices).sort((a, b) => a.name.localeCompare(b.name))}
              label={t(multiDevice ? 'deviceTitle' : 'reportDevice')}
              value={multiDevice ? deviceIds : deviceId || ''}
              onChange={(e) =>
                dispatch(
                  multiDevice
                    ? devicesActions.selectIds(e.target.value)
                    : devicesActions.selectId(e.target.value)
                )
              }
              multiple={multiDevice}
            />
          </FormControl>
        </div>
      )}

      {includeGroups && (
        <div className={classes.filterItem}>
          <FormControl fullWidth>
            <InputLabel>{t('settingsGroups')}</InputLabel>
            <Select
              label={t('settingsGroups')}
              value={groupIds}
              onChange={(e) => dispatch(reportsActions.updateGroupIds(e.target.value))}
              multiple
            >
              {Object.values(groups)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
      )}

      {button !== 'schedule' ? (
        <>
          <div className={classes.filterItem}>
            <FormControl fullWidth>
              <InputLabel>{t('reportPeriod')}</InputLabel>
              <Select
                label={t('reportPeriod')}
                value={period}
                onChange={(e) => dispatch(reportsActions.updatePeriod(e.target.value))}
              >
                {periodOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(PERIOD_LABELS[option])}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {periodOptions.includes('singleDate') && period === 'singleDate' && (
            <div className={classes.filterItem}>
              <TextField
                label={t('reportDate')}
                type="date"
                value={moment(from).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const date = moment(e.target.value, 'YYYY-MM-DD');
                  dispatch(reportsActions.updateFrom(date.startOf('day').toISOString()));
                  dispatch(reportsActions.updateTo(date.endOf('day').toISOString()));
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </div>
          )}

          <LocalizationProvider dateAdapter={AdapterMoment}>
            {periodOptions.includes('custom') && period === 'custom' && (
              <>
                <div className={classes.filterItem}>
                  <DateTimePicker
                    label={t('reportFrom')}
                    value={from ? moment(from, moment.HTML5_FMT.DATETIME_LOCAL) : null}
                    onChange={(newValue) => dispatch(
                      reportsActions.updateFrom(newValue ? newValue.format(moment.HTML5_FMT.DATETIME_LOCAL) : ''),
                    )}
                    ampm={false}
                    format="DD/MM/YYYY HH:mm"
                    slotProps={{ textField: { fullWidth: true } }}
                    desktopModeMediaQuery="@media all"
                  />
                </div>
                <div className={classes.filterItem}>
                  <DateTimePicker
                    label={t('reportTo')}
                    value={to ? moment(to, moment.HTML5_FMT.DATETIME_LOCAL) : null}
                    onChange={(newValue) => dispatch(
                      reportsActions.updateTo(newValue ? newValue.format(moment.HTML5_FMT.DATETIME_LOCAL) : ''),
                    )}
                    ampm={false}
                    format="DD/MM/YYYY HH:mm"
                    slotProps={{ textField: { fullWidth: true } }}
                    desktopModeMediaQuery="@media all"
                  />
                </div>
              </>
            )}

          </LocalizationProvider>
        </>
      ) : (
        <>
          <div className={classes.filterItem}>
            <TextField
              value={description || ''}
              onChange={(event) => setDescription(event.target.value)}
              label={t('sharedDescription')}
              fullWidth
            />
          </div>
          <div className={classes.filterItem}>
            <SelectField
              value={calendarId}
              onChange={(event) => setCalendarId(Number(event.target.value))}
              endpoint="/api/calendars"
              label={t('sharedCalendar')}
              fullWidth
            />
          </div>
        </>
      )}

      {children}

      {unified && (
        <div className={classes.filterItem}>
          <FormControl fullWidth>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={unify}
                    onChange={(e) => setUnify(e.target.checked)}
                  />
                }
                label="Unificar"
              />
            </FormGroup>
          </FormControl>
        </div>
      )}

      {extra && <div className={classes.filterItem}>{extra}</div>}

      <div className={classes.filterItem}>
        {showOnly ? (
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            disabled={disabled}
            onClick={() => handleClick('json')}
          >
            <Typography variant="button" noWrap>
              {t('reportShow')}
            </Typography>
          </Button>
        ) : (
          <SplitButton
            fullWidth
            variant="outlined"
            color="secondary"
            disabled={disabled}
            onClick={handleClick}
            selected={button}
            setSelected={setButton}
            options={
              readonly
                ? {
                  json: t('reportShow'),
                  export: t('reportExport'),
                  mail: t('reportEmail'),
                }
                : {
                  json: t('reportShow'),
                  export: t('reportExport'),
                  mail: t('reportEmail'),
                  schedule: t('reportSchedule'),
                }
            }
          />
        )}
      </div>
    </div>
  );
};

export default ReportFilter;